import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Chip, IconButton, Tooltip,
  CircularProgress, Avatar, TextField, InputAdornment,
  Select, MenuItem, FormControl, InputLabel, Grid,
} from '@mui/material';
import {
  Refresh, Search, Speed, ElectricScooter, GpsFixed,
  GpsOff, ArrowBack, BatteryChargingFull,
  DirectionsCar, TrendingUp,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { InvokeApi, apiList } from '../services/ApiService';
import { Config } from '../config/Config';

const GOOGLE_MAPS_API_KEY = 'AIzaSyBEkY7qnLR8cl0P2_dhmy5ytsZTxCTG67M';

const STATUS_COLOR = {
  MOVING:  { bg: '#dcfce7', color: '#16a34a', label: 'Moving' },
  STOPPED: { bg: '#fef3c7', color: '#d97706', label: 'Stopped' },
  IDLE:    { bg: '#e0f2fe', color: '#0284c7', label: 'Idle' },
  UNKNOWN: { bg: '#f1f5f9', color: '#64748b', label: 'Unknown' },
};

export default function LoconavTracking() {
  const navigate    = useNavigate();
  const mapRef      = useRef(null);
  const gMapRef     = useRef(null);
  const markersRef  = useRef({});
  const infoWinRef  = useRef(null);

  const [vehicles,     setVehicles]     = useState([]);
  const [telematics,   setTelematics]   = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [refreshing,   setRefreshing]   = useState(false);
  const [mapLoaded,    setMapLoaded]    = useState(false);
  const [search,       setSearch]       = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selected,     setSelected]     = useState(null);
  const [autoRefresh,  setAutoRefresh]  = useState(true);
  const intervalRef = useRef(null);

  // ── Drawer state ────────────────────────────────────────────────────────────
  // Monthly summary drawer removed — live tracking only

  // ── Load vehicles list (Loconav IDs + DB data) ─────────────────────────────
  const loadVehicles = useCallback(async () => {
    const res = await InvokeApi(Config.base_url + apiList.loconavVehicles, { page: 1, per_page: 100 });
    if (res?.code === '200') setVehicles(res.vehicles || []);
  }, []);

  // ── Load live telematics ────────────────────────────────────────────────────
  const loadTelematics = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    const res = await InvokeApi(Config.base_url + apiList.loconavTelematics, { page: 1, per_page: 100 });
    if (res?.code === '200') setTelematics(res.telematics || []);
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => { loadVehicles(); loadTelematics(); }, [loadVehicles, loadTelematics]);

  // ── Auto-refresh 30s ────────────────────────────────────────────────────────
  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (autoRefresh) intervalRef.current = setInterval(() => loadTelematics(true), 30000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [autoRefresh, loadTelematics]);

  // ── Load Google Maps ────────────────────────────────────────────────────────
  useEffect(() => {
    if (window.google?.maps) { setMapLoaded(true); return; }
    const s   = document.createElement('script');
    s.src     = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}`;
    s.async   = true;
    s.onload  = () => setMapLoaded(true);
    document.head.appendChild(s);
  }, []);

  // ── Init/update map ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapLoaded || !mapRef.current || !window.google) return;
    if (!gMapRef.current) {
      gMapRef.current = new window.google.maps.Map(mapRef.current, {
        center: { lat: 12.97, lng: 77.59 }, zoom: 11,
        mapTypeControl: true, streetViewControl: false, fullscreenControl: true,
        styles: [{ featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] }],
      });
      infoWinRef.current = new window.google.maps.InfoWindow();
    }

    const telemMap = {};
    telematics.forEach(t => { telemMap[t.vehicle_number] = t; });

    telematics.forEach(t => {
      if (!t.lat || !t.lng) return;
      const isMoving = t.movement === 'MOVING';
      const ignOn    = t.ignition === 'ON';
      const dotColor = isMoving ? '#16a34a' : ignOn ? '#f59e0b' : '#64748b';
      const icon = {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 10, fillColor: dotColor, fillOpacity: 1,
        strokeColor: '#fff', strokeWeight: 2,
      };

      if (markersRef.current[t.vehicle_number]) {
        markersRef.current[t.vehicle_number].setPosition({ lat: t.lat, lng: t.lng });
        markersRef.current[t.vehicle_number].setIcon(icon);
      } else {
        const marker = new window.google.maps.Marker({
          position: { lat: t.lat, lng: t.lng }, map: gMapRef.current,
          title: t.vehicle_number, icon,
          animation: window.google.maps.Animation.DROP,
          label: { text: t.vehicle_number, fontSize: '10px', fontWeight: '700', color: '#1a237e' },
        });
        marker.addListener('click', () => {
          infoWinRef.current.setContent(buildInfoContent(t));
          infoWinRef.current.open(gMapRef.current, marker);
          setSelected(t.vehicle_number);
        });
        markersRef.current[t.vehicle_number] = marker;
      }
    });

    if (!loading && telematics.length > 0 && Object.keys(markersRef.current).length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      Object.values(markersRef.current).forEach(m => bounds.extend(m.getPosition()));
      gMapRef.current.fitBounds(bounds);
    }
  }, [mapLoaded, telematics, loading]);

  const buildInfoContent = (t) => `
    <div style="font-family:system-ui;padding:8px;min-width:180px">
      <div style="font-size:14px;font-weight:800;color:#1a237e;margin-bottom:6px">${t.vehicle_number}</div>
      <div style="font-size:12px;color:#475569;margin-bottom:3px">
        <b>Status:</b> <span style="color:${STATUS_COLOR[t.movement]?.color||'#64748b'}">${STATUS_COLOR[t.movement]?.label||t.movement}</span>
      </div>
      <div style="font-size:12px;color:#475569;margin-bottom:3px"><b>Ignition:</b> ${t.ignition}</div>
      <div style="font-size:12px;color:#475569;margin-bottom:3px"><b>Speed:</b> ${t.speed} km/h</div>
      <div style="font-size:11px;color:#94a3b8;margin-top:4px">Last: ${t.last_updated||'—'}</div>
    </div>`;

  // ── Merged list ─────────────────────────────────────────────────────────────
  const merged = vehicles.map(v => {
    const t = telematics.find(t => t.vehicle_number === v.number) || {};
    return { ...v, ...t };
  });

  const filtered = merged.filter(v => {
    const q      = search.toLowerCase();
    const matchQ = !q || v.number?.toLowerCase().includes(q) || v.assigned_user?.toLowerCase().includes(q);
    const matchS = filterStatus === 'all'
      || (filterStatus === 'MOVING'  && v.movement === 'MOVING')
      || (filterStatus === 'STOPPED' && v.movement === 'STOPPED')
      || (filterStatus === 'IGN_ON'  && v.ignition === 'ON');
    return matchQ && matchS;
  });

  const panTo = (v) => {
    if (!gMapRef.current || !v.lat || !v.lng) return;
    gMapRef.current.panTo({ lat: v.lat, lng: v.lng });
    gMapRef.current.setZoom(16);
    const marker = markersRef.current[v.vehicle_number || v.number];
    if (marker && infoWinRef.current) {
      infoWinRef.current.setContent(buildInfoContent(v));
      infoWinRef.current.open(gMapRef.current, marker);
    }
    setSelected(v.vehicle_number || v.number);
  };

  const summary = {
    total:   merged.length,
    moving:  merged.filter(v => v.movement === 'MOVING').length,
    stopped: merged.filter(v => v.movement === 'STOPPED').length,
    ignOn:   merged.filter(v => v.ignition === 'ON').length,
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
        <Tooltip title="Back">
          <IconButton onClick={() => navigate('/dashboard')} sx={{ border: '1px solid #e8eaf6', borderRadius: 2 }}>
            <ArrowBack sx={{ color: '#1a237e' }} />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={800} color="#1a237e">Live Vehicle Tracking</Typography>
          <Typography variant="body2" color="text.secondary">{summary.total} vehicles</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip label={autoRefresh ? 'Auto ·30s' : 'Manual'} size="small"
            color={autoRefresh ? 'success' : 'default'}
            onClick={() => setAutoRefresh(a => !a)} sx={{ fontWeight: 600, cursor: 'pointer' }} />
          <Tooltip title="Refresh now">
            <IconButton onClick={() => loadTelematics(true)} sx={{ border: '1px solid #e8eaf6', borderRadius: 2 }}>
              <Refresh sx={{ animation: refreshing ? 'spin 1s linear infinite' : 'none', color: '#1a237e' }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Summary cards */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {[
          { label: 'Total',   val: summary.total,   color: '#1a237e', bg: '#eff6ff' },
          { label: 'Moving',  val: summary.moving,  color: '#16a34a', bg: '#f0fdf4' },
          { label: 'Stopped', val: summary.stopped, color: '#d97706', bg: '#fffbeb' },
          { label: 'Ign ON',  val: summary.ignOn,   color: '#0284c7', bg: '#f0f9ff' },
        ].map(({ label, val, color, bg }) => (
          <Grid item xs={6} sm={3} key={label}>
            <Card sx={{ borderRadius: 1, border: `1px solid ${color}22`, bgcolor: bg, boxShadow: 'none' }}>
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography fontWeight={800} fontSize={22} sx={{ color }}>{val}</Typography>
                <Typography fontSize={11} color="text.secondary" fontWeight={600}>{label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2}>
        {/* Sidebar */}
        <Grid item xs={12} md={3.5}>
          <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden', height: 620, }}>
            <Box sx={{ p: 1.5, borderBottom: '1px solid #f0f0f0', display: 'flex', flexDirection: 'column', gap: 1 }}>
              <TextField size="small" placeholder="Search vehicle / user…" value={search}
                onChange={e => setSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
              <FormControl size="small">
                <InputLabel>Filter</InputLabel>
                <Select value={filterStatus} label="Filter" onChange={e => setFilterStatus(e.target.value)}>
                  <MenuItem value="all">All Vehicles</MenuItem>
                  <MenuItem value="MOVING">Moving</MenuItem>
                  <MenuItem value="STOPPED">Stopped</MenuItem>
                  <MenuItem value="IGN_ON">Ignition ON</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ overflowY: 'auto', height: 520, paddingBottom: 2 }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 5 }}>
                  <CircularProgress size={28} sx={{ color: '#1a237e' }} />
                </Box>
              ) : filtered.length === 0 ? (
                <Box sx={{ textAlign: 'center', pt: 6 }}>
                  <Typography color="text.secondary" fontSize={13}>No vehicles found</Typography>
                </Box>
              ) : (
                filtered.map(v => {
                  const vnum  = v.vehicle_number || v.number || '';
                  const isSel = selected === vnum;
                  const sm    = STATUS_COLOR[v.movement] || STATUS_COLOR.UNKNOWN;
                  const hasGPS = !!(v.lat && v.lng);

                  return (
                    <Box key={v.id || vnum}
                      sx={{
                        px: 1.5, py: 1.2,
                        bgcolor: isSel ? '#e8eaf6' : 'transparent',
                        borderLeft: isSel ? '3px solid #1a237e' : '3px solid transparent',
                        borderBottom: '1px solid #f8fafc',
                        transition: 'all .15s',
                        '&:hover': { bgcolor: '#f5f7ff' },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                          onClick={() => hasGPS && panTo(v)}
                          sx={{
                            width: 36, height: 36, flexShrink: 0, cursor: hasGPS ? 'pointer' : 'default',
                            background: 'linear-gradient(135deg,#1a237e,#1565c0)', fontSize: 11, fontWeight: 700,
                          }}
                        >
                          <ElectricScooter sx={{ fontSize: 18 }} />
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography
                              fontWeight={700} fontSize={13} noWrap sx={{ fontFamily: 'monospace', cursor: hasGPS ? 'pointer' : 'default' }}
                              onClick={() => hasGPS && panTo(v)}
                            >
                              {vnum}
                            </Typography>
                            {hasGPS ? <GpsFixed sx={{ fontSize: 12, color: '#16a34a' }} /> : <GpsOff sx={{ fontSize: 12, color: '#94a3b8' }} />}
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.2 }}>
                            <Chip label={sm.label} size="small"
                              sx={{ height: 16, fontSize: 9, fontWeight: 700, bgcolor: sm.bg, color: sm.color }} />
                            <Chip label={`IGN ${v.ignition || '—'}`} size="small"
                              sx={{ height: 16, fontSize: 9, fontWeight: 700,
                                bgcolor: v.ignition === 'ON' ? '#dcfce7' : '#f1f5f9',
                                color:   v.ignition === 'ON' ? '#16a34a' : '#94a3b8' }} />
                          </Box>
                          {v.speed > 0 && <Typography fontSize={10} color="text.secondary"><Speed sx={{ fontSize: 10, mr: 0.3 }} />{v.speed} km/h</Typography>}
                          {v.assigned_user && <Typography fontSize={10} color="text.secondary" noWrap>👤 {v.assigned_user}</Typography>}
                          {v.vendor_name    && <Typography fontSize={10} color="text.secondary" noWrap>🏢 {v.vendor_name}</Typography>}
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                          {v.db_battery != null && (
                            <>
                              <BatteryChargingFull sx={{ fontSize: 14, color: v.db_battery > 50 ? '#16a34a' : '#f59e0b' }} />
                              <Typography fontSize={9} color="text.secondary">{v.db_battery}%</Typography>
                            </>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  );
                })
              )}
            </Box>
          </Card>
        </Grid>

        {/* Map */}
        <Grid item xs={12} md={8.5}>
          <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden', height: 620 }}>
            {!mapLoaded && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress sx={{ color: '#1a237e' }} />
              </Box>
            )}
            <Box ref={mapRef} sx={{ width: '100%', height: '100%', display: mapLoaded ? 'block' : 'none' }} />
          </Card>
        </Grid>
      </Grid>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </Box>
  );
}
